import { CONVERSATION_PAGE_MAX, type ProjectDetail } from "@esi/shared";
import { FileText, MessagesSquare, ScrollText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterChips } from "@/components/FilterChips";
import { useConversationPage } from "../../conversation/queries.ts";
import { formatAgo } from "../../i18n/format.ts";
import { conversationPath, projectHomePath, wikiPath } from "../../router.tsx";
import { useWikiActivity, useWikiIndex } from "../../wiki/queries.ts";
import { can } from "../capabilities.ts";
import { useDocuments, useMembers } from "../queries.ts";
import { HomeEmptyLine, HomeRow, HomeRows, HomeSection, stateOf } from "./HomeSection.tsx";
import {
  publicationsFromActivity,
  publicationsFromLog,
  type WhatChangedFilter,
  type WhatChangedRow,
  whatChangedRows,
} from "./what-changed.ts";

/** The conversations shared with the project, as What changed asks for them. */
// The list is ordered by last activity, not by share time: a full page (50) leaves room for the
// 10 latest shares among them (an exact share-time order needs the server, slice B).
export const projectShares = (projectId: string) =>
  ({ projectId, filter: "project", limit: CONVERSATION_PAGE_MAX }) as const;

/**
 * What changed (spec `project-home` "What changed"): the 10 latest events the viewer can read.
 * Publications come from the change-set activity for viewers who may see drafts (one row per
 * set), else from the index log; conversations shared with the project are requested only by
 * those the share reaches (readers and up: guests are never in a project share's audience).
 */
export function WhatChanged({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<WhatChangedFilter>("all");
  const viewDrafts = can(project, "wiki.view_drafts");
  // The share reaches readers and up, as `conversation.share` (READER_UP) does.
  const reached = can(project, "conversation.share");
  const index = useWikiIndex(project.id);
  const activity = useWikiActivity(project.id, viewDrafts);
  const documents = useDocuments(project.id);
  const shares = useConversationPage(projectShares(project.id), { enabled: reached });
  // Names for "added by": the People section's list, for those who may see members.
  const members = useMembers(project.id, can(project, "project.view_members"));
  const queries = [
    ...(viewDrafts ? [activity] : []),
    index,
    documents,
    ...(reached ? [shares] : []),
  ];
  // The activity's first page (50 sets by last update) may hold no publication after much
  // drafting: the index log, reader-filtered too, then gives them page by page.
  const grouped = viewDrafts ? publicationsFromActivity(activity.data?.pages[0]?.items ?? []) : [];
  const publications = grouped.length > 0 ? grouped : publicationsFromLog(index.data?.log ?? []);
  const sources = {
    publications,
    documents: documents.data?.documents ?? [],
    conversations: reached ? (shares.data?.items ?? []) : [],
  };
  const rows = whatChangedRows(sources, filter);
  const names = new Map(members.data?.map((m) => [m.userId.toLowerCase(), m.displayName]));
  const options = [
    { value: "all" as const, label: t("projectOverview.whatChanged.all") },
    { value: "wiki" as const, label: t("project.tabs.wiki") },
    { value: "sources" as const, label: t("project.tabs.files") },
    ...(reached
      ? [{ value: "conversations" as const, label: t("projectHome.tabs.conversations") }]
      : []),
  ];
  return (
    <HomeSection
      id="what-changed"
      title={t("projectOverview.whatChanged.title")}
      state={stateOf(queries, 4)}
      toolbar={
        // From `sm` up only: on phones the list stays one quiet feed.
        <FilterChips
          label={t("projectOverview.whatChanged.filter")}
          options={options}
          value={filter}
          onChange={setFilter}
          className="max-sm:hidden"
        />
      }
      foot={t("projectOverview.whatChanged.readable")}
    >
      {rows.length === 0 ? (
        <HomeEmptyLine>
          {filter === "all"
            ? t("projectOverview.whatChanged.empty")
            : t("projectOverview.whatChanged.emptyFilter")}
        </HomeEmptyLine>
      ) : (
        <HomeRows>
          {rows.map((row) => (
            <WhatChangedItem key={row.id} project={project} row={row} names={names} />
          ))}
        </HomeRows>
      )}
    </HomeSection>
  );
}

function WhatChangedItem(props: {
  project: ProjectDetail;
  row: WhatChangedRow;
  names: Map<string, string | null>;
}) {
  const { project, row } = props;
  const { t } = useTranslation();
  const at = { at: row.at, atLabel: formatAgo(row.at) };
  switch (row.kind) {
    case "wiki":
      return (
        <HomeRow
          icon={ScrollText}
          href={wikiPath(project.id, row.pageId)}
          title={
            row.more > 0
              ? t("projectOverview.whatChanged.publishedMore", {
                  title: row.title,
                  count: row.more,
                })
              : row.title
          }
          meta={[
            t("projectOverview.whatChanged.published"),
            row.by ? t("projectOverview.by", { name: row.by }) : null,
          ]}
          {...at}
        />
      );
    case "source": {
      const name = props.names.get(row.document.uploadedBy.toLowerCase());
      return (
        <HomeRow
          icon={FileText}
          href={`${projectHomePath(project.id, "files")}?document=${encodeURIComponent(row.document.id)}`}
          title={row.document.title}
          meta={[
            t("projectOverview.whatChanged.sourceAdded"),
            name ? t("projectOverview.by", { name }) : null,
          ]}
          {...at}
        />
      );
    }
    case "conversation":
      return (
        <HomeRow
          icon={MessagesSquare}
          href={conversationPath(row.conversation)}
          title={row.conversation.title ?? t("conversation.untitled")}
          meta={[
            t("projectOverview.whatChanged.shared"),
            row.by ? t("projectOverview.by", { name: row.by }) : null,
          ]}
          {...at}
        />
      );
  }
}
